/**
 * App Component
 * 
 * Main application component that manages state and coordinates
 * the interaction between QuestionInput, ResponseDisplay, and LinkSection components.
 * 
 * Requirements: 1.2, 1.4, 1.5, 10.5
 */

import React, { useState } from 'react';
import './App.css';
import { Authenticator } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import { signOut } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { QuestionInput, ResponseDisplay, LinkSection, Profile, Quiz, StreamingChat } from './components';
import { submitQuestion } from './services';
import { AssistantResponse } from './types';
import { awsConfig } from './config/aws-config';

// Configure Amplify
Amplify.configure(awsConfig);

interface ConversationEntry {
  question: string;
  response: AssistantResponse | null;
  error: string | null;
  timestamp: Date;
}

function App() {
  // State management for current interaction
  const [response, setResponse] = useState<AssistantResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Conversation history state
  const [conversationHistory, setConversationHistory] = useState<ConversationEntry[]>([]);
  
  // Profile modal state
  const [showProfile, setShowProfile] = useState<boolean>(false);
  
  // Streaming mode toggle
  const [useStreaming, setUseStreaming] = useState<boolean>(true);
  
  // Hamburger menu state
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  /**
   * Handle question submission
   * Manages loading state transitions and error handling
   */
  const handleSubmit = async (submittedQuestion: string) => {
    // Reset previous state
    setError(null);
    setResponse(null);
    
    // Set loading state
    setIsLoading(true);

    try {
      // Call API service
      const apiResponse = await submitQuestion(submittedQuestion);
      
      // Update response state
      setResponse(apiResponse);
      
      // Add to conversation history
      setConversationHistory(prev => [
        ...prev,
        {
          question: submittedQuestion,
          response: apiResponse,
          error: null,
          timestamp: new Date()
        }
      ]);
    } catch (err) {
      // Handle error states
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      
      // Add error to conversation history
      setConversationHistory(prev => [
        ...prev,
        {
          question: submittedQuestion,
          response: null,
          error: errorMessage,
          timestamp: new Date()
        }
      ]);
      
      // Log error to console for debugging
      console.error('Error submitting question:', err);
    } finally {
      // Clear loading state
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <Authenticator>
      {({ user }) => (
        <div className="App">
          <header className="app-header">
            <div className="header-content">
              <div>
                <h1>AI Learning Assistant</h1>
                <p>Ask questions about AI and machine learning and get intelligent answers with learning resources</p>
              </div>
              <div className="user-menu">
                <button 
                  className="hamburger-button" 
                  onClick={toggleMenu}
                  aria-label="Menu"
                >
                  ☰
                </button>
                {menuOpen && (
                  <>
                    <div className="menu-overlay" onClick={closeMenu}></div>
                    <div className="menu-dropdown">
                      <div className="menu-item user-email-item">
                        {user?.signInDetails?.loginId}
                      </div>
                      <button 
                        onClick={() => {
                          setUseStreaming(!useStreaming);
                          closeMenu();
                        }} 
                        className="menu-item menu-button"
                      >
                        {useStreaming ? '⚡ Streaming Mode' : '📄 Standard Mode'}
                      </button>
                      <button 
                        onClick={() => {
                          setShowProfile(true);
                          closeMenu();
                        }} 
                        className="menu-item menu-button"
                      >
                        Profile
                      </button>
                      <button 
                        onClick={() => {
                          handleSignOut();
                          closeMenu();
                        }} 
                        className="menu-item menu-button"
                      >
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <main className="app-main">
            {useStreaming ? (
              /* Streaming Mode */
              <section className="streaming-section">
                <StreamingChat />
              </section>
            ) : (
              /* Non-Streaming Mode (Legacy) */
              <>
                {/* Question Input Component */}
                <section className="input-section">
                  <QuestionInput onSubmit={handleSubmit} isLoading={isLoading} />
                </section>

                {/* Current Response Display */}
                <section className="response-section">
                  <ResponseDisplay 
                    answer={response?.answer || null} 
                    isLoading={isLoading} 
                    error={error} 
                  />
                  
                  {/* Quiz Section */}
                  {response && response.quiz && (
                    <Quiz quiz={response.quiz} />
                  )}
                  
                  {/* Documentation Links Section */}
                  {response && response.documentationLinks && (
                    <LinkSection 
                      title="Documentation & Resources" 
                      links={response.documentationLinks} 
                    />
                  )}
                  
                  {/* Training Links Section */}
                  {response && response.trainingLinks && (
                    <LinkSection 
                      title="Online Courses" 
                      links={response.trainingLinks} 
                    />
                  )}
                </section>

                {/* Conversation History */}
                {conversationHistory.length > 1 && (
                  <section className="history-section">
                    <h2>Previous Questions</h2>
                    <div className="history-list">
                      {conversationHistory.slice(0, -1).reverse().map((entry, index) => (
                        <div key={index} className="history-entry">
                          <div className="history-question">
                            <strong>Q:</strong> {entry.question}
                          </div>
                          {entry.error ? (
                            <div className="history-error">
                              <strong>Error:</strong> {entry.error}
                            </div>
                          ) : entry.response && (
                            <div className="history-answer">
                              <strong>A:</strong> {entry.response.answer.substring(0, 150)}...
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </main>

          <footer className="app-footer">
            <p>Powered by Claude AI</p>
          </footer>

          {/* Profile Modal */}
          {showProfile && <Profile onClose={() => setShowProfile(false)} />}
        </div>
      )}
    </Authenticator>
  );
}

export default App;
